import {forwardRef} from 'react';
import {Button, Icon} from '@ezcater/tapas';
import {faAngleDown, faCircleUser} from '@fortawesome/pro-regular-svg-icons';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import useCorporateAccount from '@/hooks/useCorporateAccount';
import {formatPoints} from '../utils';
import {UserProfileButtonDisplayMode} from './types';

type UserProfileButtonProps = {
  firstName: string;
  displayMode: UserProfileButtonDisplayMode;
} & React.ComponentPropsWithoutRef<'button'>;

type Ref = HTMLButtonElement;

const UserProfileButton = forwardRef<Ref, UserProfileButtonProps>(function UserProfileButton(
  {firstName, displayMode, ...props},
  ref,
) {
  const {t} = useTranslation('app-bar');
  const {corporateLogoIconUrl, corporateDisplayName} = useCorporateAccount();

  const renderSubtitle = () => {
    switch (displayMode.mode) {
      case 'corporate':
        return (
          <div className="w-full truncate whitespace-nowrap text-left text-xs text-peppercorn-500">
            {displayMode.displayName}
          </div>
        );
      case 'ezRewards':
        return (
          <div className="text-ellipsis whitespace-nowrap text-xs text-kiwi-400">
            {t('user.pts', {balance: formatPoints(displayMode.balance)})}
          </div>
        );
      case 'none':
        return null;
    }
  };

  return (
    <Button
      className="flex h-12 w-[125px] items-center justify-between border-peppercorn-100 p-2"
      data-testid="profile-nav-bar-item"
      ref={ref}
      variant="outlined"
      {...props}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        {displayMode.mode === 'corporate' && corporateLogoIconUrl ? (
          <Image
            src={corporateLogoIconUrl}
            alt={`${corporateDisplayName || 'Company'} logo`}
            className="shrink-0"
            width={24}
            height={24}
          />
        ) : (
          <Icon className="shrink-0" icon={faCircleUser} size="small" />
        )}

        <div className="flex flex-col items-start overflow-hidden">
          <div className="w-full truncate whitespace-nowrap text-left text-sm">{firstName}</div>
          {renderSubtitle()}
        </div>
      </div>

      <Icon className="shrink-0" icon={faAngleDown} size="small" />
    </Button>
  );
});

export default UserProfileButton;
